
<v-container
	fluid
	class="system-list-container"
>
	<v-row justify="center">
		<v-col
			cols="12"
			md="12"
			lg="12"
			xl="8"
			class="system-list"
		>
			<!-- Search Filter -->
			<div class="search-filter-container mb-6">
				<div class="d-flex align-center gap-3">
					<v-text-field
						v-model="searchQuery"
						label="Search systems..."
						placeholder="Search by system or service name"
						prepend-inner-icon="mdi-magnify"
						clearable
						outlined
						dense
						hide-details
						class="search-input flex-grow-1"
						data-testid="system-search-input"
					/>
					<div class="sort-container">
						<span class="sort-label">
							Sort:
						</span>
						<v-btn-toggle
							v-model="sortMode"
							class="sort-toggle"
							mandatory
							borderless
							color="primary"
							dense
						>
							<v-tooltip bottom>
								<template #activator="{ on, attrs }">
									<v-btn
										value="newest"
										data-testid="sort-newest-button"
										class="sort-btn"
										x-small
										v-bind="attrs"
										v-on="on"
									>
										<v-icon size="14">
											mdi-clock-time-four-outline
										</v-icon>
									</v-btn>
								</template>
								<span>
									Sort by newest first
								</span>
							</v-tooltip>
							<v-tooltip bottom>
								<template #activator="{ on, attrs }">
									<v-btn
										value="alphabetical"
										data-testid="sort-alphabetical-button"
										class="sort-btn"
										x-small
										v-bind="attrs"
										v-on="on"
									>
										<v-icon size="14">
											mdi-sort-alphabetical-variant
										</v-icon>
									</v-btn>
								</template>
								<span>
									Sort alphabetically (A-Z)
								</span>
							</v-tooltip>
						</v-btn-toggle>
					</div>
					<v-spacer />
					<v-btn
						v-if="userHasFullConnectorServicePermission"
						color="primary"
						depressed
						data-testid="add-system-button"
						class="primary-action-button text-none"
						@click="handleCreateSystem"
					>
						<v-icon left>
							mdi-plus
						</v-icon>
						Add System
					</v-btn>
				</div>
			</div>

			<!-- System Cards List -->
			<div class="system-cards-list">
				<div
					v-if="filteredSystems.length === 0 && searchQuery"
					class="empty-search-state"
				>
					<!-- Empty Search State -->
					<v-card
						class="empty-state-card text-center pa-6"
						outlined
					>
						<v-icon
							size="40"
							color="grey lighten-2"
							class="mb-3"
						>
							mdi-file-search-outline
						</v-icon>
						<h4 class="mb-2 grey--text text--darken-1">
							No systems found
						</h4>
						<p class="mb-4 grey--text text--lighten-1 text-body-2">
							No systems match your search for "{{ searchQuery }}". Try adjusting your search terms.
						</p>
						<v-btn
							text
							color="primary"
							small
							@click="clearSearch"
						>
							Clear Search
						</v-btn>
					</v-card>
				</div>          <div
					v-else-if="systems.length === 0"
					class="empty-state"
				>
					<!-- Create System Card (when no systems exist) -->
					<div class="create-system-container">
						<v-card
							class="system-create-card"
							outlined
							hover
							@click="handleCreateSystem"
						>
							<div class="create-card-content">
								<div class="create-icon-container">
									<v-icon
										color="primary"
										size="48"
									>
										mdi-plus-circle
									</v-icon>
								</div>
								<div class="create-content">
									<h3 class="create-title">
										Create New System
									</h3>
									<p class="create-subtitle">
										Set up a new system to organize your integrations
									</p>
								</div>
								<div class="create-action">
									<v-btn
										color="primary"
										class="text-none"
										:disabled="isCreatingSystem"
										@click="handleCreateSystem"
									>
										{{ isCreatingSystem ? 'Creating...' : 'Get Started' }}
									</v-btn>
								</div>
							</div>
						</v-card>
					</div>
				</div>

				<div v-else>
					<!-- System Cards -->
					<div
						v-for="system in sortedSystems"
						:key="system.systemIntegration.id"
						class="system-item mb-6"
					>
						<SystemCard
							:system-integration="system.systemIntegration"
							:integration-services="system.integrationServices"
							:max-integration-services="maxServicesPerSystem"
							:user-has-log-view-permission="userHasLogViewPermission"
							:user-has-full-connector-service-permission="userHasFullConnectorServicePermission"
							:style="system.systemIntegration.iconColor && system.systemIntegration.iconColor !== 'white' ? { '--icon-color': system.systemIntegration.iconColor } : {}"
							@toggle-system-enabled="$emit('toggle-system-enabled', $event)"
							@toggle-service-enabled="$emit('toggle-service-enabled', $event)"
							@view-service-details="$emit('view-service-details', $event)"
							@edit-service="editService"
							@clone-service="cloneService"
							@view-failures="$emit('view-failures', $event)"
							@delete-service="$emit('delete-service', $event)"
							@edit-system="$emit('edit-system', $event)"
							@export-system="$emit('export-system', $event)"
							@export-service="$emit('export-service', $event)"
							@clone-system="$emit('clone-system', $event)"
							@delete-system="$emit('delete-system', $event)"
							@change:active="$emit('toggle-system-enabled', $event)"
							@create-service="$emit('create-service', $event)"
							@reorder-services="$emit('reorder-services', $event)"
							@upgrade-license="$emit('upgrade-license')"
						/>
					</div>

					<!-- Create System Button -->
					<div
						v-if="showCreateButton"
						class="create-system-container mt-6"
					>
						<v-card
							class="system-create-card"
							outlined
							hover
							@click="handleCreateSystem"
						>
							<div class="create-card-content">
								<div class="create-icon-container">
									<v-icon
										color="primary"
										size="48"
									>
										mdi-plus-circle
									</v-icon>
								</div>
								<div class="create-content">
									<h3 class="create-title">
										Create New System
									</h3>
									<p class="create-subtitle">
										Set up a new system to organize your integrations
									</p>
								</div>
								<div class="create-action">
									<v-btn
										color="primary"
										:disabled="isCreatingSystem"
										@click="handleCreateSystem"
									>
										{{ isCreatingSystem ? 'Creating...' : 'Get Started' }}
									</v-btn>
								</div>
							</div>
						</v-card>
					</div>
				</div>
			</div>
		</v-col>
	</v-row>
</v-container>
